import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import HexBase64Tool from '../components/HexBase64Tool';
import FaqSection from '../components/Faq';
import { PageHero } from '../components/InfoSections';
import { useTheme } from '../theme';
import { softwareApplicationSchema, faqPageSchema, breadcrumbSchema, Faq } from '../lib/schema';

const FAQS: Faq[] = [
  { q: 'How do I convert hex to Base64?', a: 'Paste a hexadecimal string above (spaces, colons and a 0x prefix are ignored). The tool parses it into bytes and re-encodes those bytes as Base64.' },
  { q: 'What is the difference between hex and Base64?', a: 'Both represent binary data as text. Hex uses 16 symbols (2 chars per byte, +100% size); Base64 uses 64 symbols (4 chars per 3 bytes, +33% size), so Base64 is more compact.' },
  { q: 'Why does my hex fail to convert?', a: 'Hex must contain only 0-9 and a-f and have an even number of digits (two per byte). Odd-length or non-hex characters are rejected.' },
  { q: 'Can I convert Base64 back to hex?', a: 'Yes — switch the direction toggle to Base64 → Hex to get the lowercase hex representation of the same bytes.' },
];

export default function HexToBase64Page() {
  const { themeClasses } = useTheme();
  return (
    <>
      <Seo
        title="Hex to Base64 Converter — and Back | EasyBase64"
        description="Convert hexadecimal to Base64 and Base64 to hex online for free. Works on raw bytes, instant and private — runs entirely in your browser."
        path="/hex-to-base64"
        jsonLd={[
          softwareApplicationSchema({ name: 'Hex to Base64 Converter', description: 'Convert hexadecimal to Base64 and back in the browser.', path: '/hex-to-base64' }),
          faqPageSchema(FAQS),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Hex to Base64', path: '/hex-to-base64' }]),
        ]}
      />
      <PageHero
        title="Hex to Base64"
        subtitle={<>Convert hexadecimal to Base64 and back, byte for byte. New to Base64? Read the <Link to="/what-is-base64" className="underline">guide</Link>.</>}
      />
      <div className="mb-12">
        <HexBase64Tool />
      </div>
      <section className={`${themeClasses.infoCard} rounded-2xl p-5 sm:p-8 border mb-12 max-w-3xl mx-auto`}>
        <h2 className={`text-2xl font-bold mb-4 ${themeClasses.textPrimary}`}>Hex vs. Base64</h2>
        <p className={`${themeClasses.textSecondary} leading-relaxed`}>
          Hexadecimal represents each byte as two characters (0–9, a–f), doubling the size. Base64 packs three bytes into
          four characters, so it is roughly 25% smaller than hex for the same data — which is why Base64 is preferred for
          embedding or transmitting binary blobs as text.
        </p>
      </section>
      <FaqSection title="Hex to Base64 — FAQ" faqs={FAQS} />
    </>
  );
}
